import React, { useCallback, useEffect, useRef, useState } from "react";
import { useApp } from "../../../../context/AppContext";
import { useAppointmentForms } from "../../../../hooks/useAppointmentForms";
import { AppointmentForm as AppointmentFormType } from "../../../../types/appointmentForms";
import type { AppointmentTag } from "../../../../types/appointmentTags";
import {
  CalendarEventType,
  EventPreviewData,
} from "../../../../types/calendar";
import { Client } from "../../../../types/client";
import { FormTemplate } from "../../../../types/formTemplates";
import {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../../types/transactions";
import { ClientDetailTab } from "../../../clients/ClientDetails";
import type { DrawerSlotApi } from "../drawer/drawerSlot";

interface UsePreviewPopoverArgs {
  salonUuid: string | undefined;
  isMobile: boolean;
  findTransaction: (id: string) => GetTransactionsResponse | undefined;
  openTransactionModal: (transaction: GetTransactionsResponse) => void;
  openTimeBlockEditor: (event: CalendarEventType) => void;
  handleTransactionUpdated: (
    transaction: GetTransactionsResponse | TransactionResponse,
  ) => Promise<void>;
  applyAppointmentStatus: (
    transaction: GetTransactionsResponse,
    newStatus: string,
  ) => Promise<GetTransactionsResponse | null>;
  /** The calendar's single drawer slot; the popover holds it as "appointment". */
  drawerSlot: Pick<
    DrawerSlotApi,
    "slot" | "open" | "close" | "openNested" | "setEvictor"
  >;
}

/**
 * The appointment drawer's state (formerly the preview popover; the hook keeps
 * its name) and the sub-surfaces it opens (client drawer, Add Form/SOAP
 * library, FormViewer, booking drawer). Opening a sub-surface
 * stashes the current popover and closes it; closing the sub-surface re-opens
 * it. Also owns calendar event click / double-click routing.
 *
 * The popover takes part in the drawer slot as kind "appointment": showing it
 * takes the slot (closing the booking drawer), hiding it releases the slot,
 * and another kind taking the slot hides it. Open/close transitions go
 * through `showPreview`; in-place patches of the shown preview use
 * setPreviewData directly.
 */
export function usePreviewPopover({
  salonUuid,
  isMobile,
  findTransaction,
  openTransactionModal,
  openTimeBlockEditor,
  handleTransactionUpdated,
  applyAppointmentStatus,
  drawerSlot,
}: UsePreviewPopoverArgs) {
  const { getTransaction } = useApp();

  const [previewData, setPreviewData] = useState<EventPreviewData | null>(null);

  // Show / hide the popover AND take / release the drawer slot.
  const {
    slot,
    open: openSlot,
    close: closeSlot,
    openNested,
    setEvictor,
  } = drawerSlot;
  const showPreview = useCallback(
    (data: EventPreviewData | null) => {
      if (data) {
        openSlot("appointment", data.transaction?.transaction.id);
      } else {
        closeSlot("appointment");
      }
      setPreviewData(data);
    },
    [openSlot, closeSlot],
  );
  // Another drawer took the slot: hide the popover (the slot is already
  // being handed over, so only the local state changes).
  useEffect(() => {
    setEvictor("appointment", () => setPreviewData(null));
  }, [setEvictor]);

  // Client drawer (opened from the preview popover). The same drawer serves the
  // client-name link (overview tab) and the "See all notes" link (notes tab).
  const [notesClient, setNotesClient] = useState<Client | null>(null);
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);
  const [clientDrawerTab, setClientDrawerTab] =
    useState<ClientDetailTab>("notes");
  // Blur-the-page wait while a timeline-clicked transaction loads, before the
  // detail modal opens over the client drawer.
  const [timelineTxnLoading, setTimelineTxnLoading] = useState(false);

  // "Add Form/SOAP" modal, lifted out of the popover so it survives the popover
  // closing. Shares assign functionality via useAppointmentForms.
  const [addFormTransaction, setAddFormTransaction] =
    useState<GetTransactionsResponse | null>(null);
  const [addFormModalOpen, setAddFormModalOpen] = useState(false);
  const addFormForms = useAppointmentForms(
    addFormTransaction?.transaction.id ?? "",
    {
      initialForms: addFormTransaction?.forms ?? [],
      salonUuid,
      clientUuid: addFormTransaction?.client?.id,
      // Keep the calendar store in sync so the popover shows the new form on
      // subsequent opens too (the immediate re-open is patched separately).
      // useAppointmentForms reads this through a ref, so an inline fn is fine.
      onFormsChange: (forms) => {
        if (addFormTransaction) {
          void handleTransactionUpdated({ ...addFormTransaction, forms });
        }
      },
    },
  );

  // FormViewer opened from a popover form row, lifted out of the popover so it
  // survives the popover closing (mirrors the Add Form/SOAP modal above). Its
  // hook keeps the calendar store in sync so the popover reflects form changes.
  const [viewFormTransaction, setViewFormTransaction] =
    useState<GetTransactionsResponse | null>(null);
  const [viewFormId, setViewFormId] = useState<string | null>(null);
  const viewFormForms = useAppointmentForms(
    viewFormTransaction?.transaction.id ?? "",
    {
      initialForms: viewFormTransaction?.forms ?? [],
      salonUuid,
      clientUuid: viewFormTransaction?.client?.id,
      onFormsChange: (forms) => {
        if (viewFormTransaction) {
          void handleTransactionUpdated({ ...viewFormTransaction, forms });
        }
      },
    },
  );
  const refetchViewForms = viewFormForms.refetch;
  const assignAddForm = addFormForms.assign;

  // Mirror previewData into a ref so the stash handlers don't need it in their
  // dependency lists, and hold the popover to re-open after a sub-surface closes.
  const previewDataRef = useRef<EventPreviewData | null>(previewData);
  useEffect(() => {
    previewDataRef.current = previewData;
  }, [previewData]);
  const reopenPreviewRef = useRef<EventPreviewData | null>(null);
  // The drawer's open nested screen (Forms / Notes / …), stashed with the
  // popover so a sub-surface (Add form, a form, the client drawer) returns to
  // it rather than the overview.
  const openNestedRef = useRef<string | null>(null);
  openNestedRef.current = slot?.kind === "appointment" ? slot.nested : null;
  const reopenNestedRef = useRef<string | null>(null);

  const closePreview = useCallback(() => showPreview(null), [showPreview]);

  const stashAndClosePreview = useCallback(() => {
    reopenPreviewRef.current = previewDataRef.current;
    reopenNestedRef.current = openNestedRef.current;
    showPreview(null);
  }, [showPreview]);

  // Forget the stashed popover (e.g. its appointment was just rescheduled).
  const dropPreviewStash = useCallback(() => {
    reopenPreviewRef.current = null;
    reopenNestedRef.current = null;
  }, []);

  const reopenPreview = useCallback(
    (patchForms?: AppointmentFormType[]) => {
      const stashed = reopenPreviewRef.current;
      const nested = reopenNestedRef.current;
      reopenPreviewRef.current = null;
      reopenNestedRef.current = null;
      if (!stashed) return;
      // Re-open immediately (no network round-trip → no lag). When Add Form/SOAP
      // returns the refreshed forms, patch them onto the stashed transaction so
      // the newly-linked form shows live — this comes straight from the compliance
      // forms endpoint, so it doesn't depend on the core GetTransactions deploy.
      if (patchForms && stashed.transaction) {
        showPreview({
          ...stashed,
          transaction: { ...stashed.transaction, forms: patchForms },
        });
      } else {
        showPreview(stashed);
      }
      if (nested) openNested(nested);
    },
    [showPreview, openNested],
  );

  // Transactions whose forms were sent this session — keeps the popover's
  // "Sent!" state across re-opens (the popover remounts each time).
  const [sentFormTxnIds, setSentFormTxnIds] = useState<Set<string>>(
    () => new Set(),
  );
  const markFormsSent = useCallback((txnId: string) => {
    setSentFormTxnIds((prev) => {
      if (prev.has(txnId)) return prev;
      const next = new Set(prev);
      next.add(txnId);
      return next;
    });
  }, []);

  const handleViewClient = useCallback(
    (client: Client) => {
      stashAndClosePreview();
      setNotesClient(client);
      setClientDrawerTab("overview");
      setNotesDrawerOpen(true);
    },
    [stashAndClosePreview],
  );

  const handleClientDrawerClose = useCallback(() => {
    setNotesDrawerOpen(false);
    reopenPreview();
  }, [reopenPreview]);

  // Timeline row in the client drawer: blur the page with the standard spinner
  // while the transaction loads, then open the calendar's OWN full-screen modal
  // over the drawer. Closing it returns to the drawer.
  const handleOpenTimelineTransaction = useCallback(
    async (transactionId: string) => {
      setTimelineTxnLoading(true);
      try {
        const txn = await getTransaction(transactionId);
        if (txn) {
          openTransactionModal(txn);
        }
      } catch (e) {
        console.error("Error loading transaction from timeline:", e);
      } finally {
        setTimelineTxnLoading(false);
      }
    },
    [getTransaction, openTransactionModal],
  );

  const handleAddForm = useCallback(
    (transaction: GetTransactionsResponse) => {
      stashAndClosePreview();
      setAddFormTransaction(transaction);
      setAddFormModalOpen(true);
    },
    [stashAndClosePreview],
  );

  const handleAddFormModalClose = useCallback(
    (newForms?: AppointmentFormType[]) => {
      setAddFormModalOpen(false);
      setAddFormTransaction(null);
      reopenPreview(newForms);
    },
    [reopenPreview],
  );

  // No-arg variant for the library's close / edit-custom / create-new props.
  const dismissAddFormModal = useCallback(
    () => handleAddFormModalClose(),
    [handleAddFormModalClose],
  );

  const handleUseTemplate = useCallback(
    async (template: FormTemplate) => {
      // Back to the drawer's Forms screen with the new form listed (not
      // opened in the FormViewer).
      const newForms = await assignAddForm(template);
      if (newForms) handleAddFormModalClose(newForms);
    },
    [assignAddForm, handleAddFormModalClose],
  );

  // Open a single form from a popover row in the FormViewer (parity with the
  // Transaction Modal). Stash + close the popover; closing the viewer re-opens it.
  const handleOpenForm = useCallback(
    (transaction: GetTransactionsResponse, formId: string) => {
      stashAndClosePreview();
      setViewFormTransaction(transaction);
      setViewFormId(formId);
    },
    [stashAndClosePreview],
  );

  const handleFormViewerChanged = useCallback(() => {
    void refetchViewForms();
  }, [refetchViewForms]);

  const handleFormViewerClose = useCallback(() => {
    const txnId = viewFormTransaction?.transaction.id;
    // Close the viewer by clearing the form id (this unmounts FormViewer) and
    // re-open the popover instantly from the stash — no network round-trip, so
    // no lag (matching the Add Form/SOAP path). Crucially we keep
    // viewFormTransaction set until the refetch resolves: viewFormForms'
    // onFormsChange (fired inside refetch, on success) is what syncs the fresh
    // status into the calendar store via handleTransactionUpdated, and its
    // guard reads the live viewFormTransaction. Nulling it here would drop the
    // store sync, so reopening the popover from scratch would show the
    // pre-interaction status (e.g. the fire-and-forget mark-opened flip).
    setViewFormId(null);
    reopenPreview();
    // Refetch in the background to reflect status changes, then patch the
    // re-opened popover and tear down the viewer transaction. fetchForms
    // returns [] on a network error (and only syncs the store on success), so
    // an empty/failed refetch leaves the seeded forms intact rather than
    // wiping them.
    void refetchViewForms().then((forms) => {
      setViewFormTransaction(null);
      if (!txnId || forms.length === 0) return;
      setPreviewData((prev) =>
        prev && prev.transaction?.transaction.id === txnId
          ? { ...prev, transaction: { ...prev.transaction, forms } }
          : prev,
      );
    });
  }, [refetchViewForms, reopenPreview, viewFormTransaction]);

  // Open the Transaction Detail modal (from the popover, list, or card view).
  const handleOpenTransactionModal = useCallback(
    (transaction: GetTransactionsResponse) => {
      showPreview(null);
      openTransactionModal(transaction);
    },
    [showPreview, openTransactionModal],
  );

  // Popover "Edit" → the Transaction Detail modal for the previewed txn.
  const handlePreviewEdit = useCallback(() => {
    const transaction = previewDataRef.current?.transaction;
    if (transaction) handleOpenTransactionModal(transaction);
  }, [handleOpenTransactionModal]);

  // Advance an appointment's status from the preview popover (Requested →
  // Accepted → Confirmed), then update the open popover in place so its status
  // badge and action button reflect the new status without closing.
  const handlePreviewStatusUpdate = useCallback(
    async (newStatus: string) => {
      const transaction = previewDataRef.current?.transaction;
      if (!transaction) return;

      const updatedTransaction = await applyAppointmentStatus(
        transaction,
        newStatus,
      );
      if (!updatedTransaction) return;

      setPreviewData((prev) =>
        prev
          ? {
              ...prev,
              event: { ...prev.event, status: newStatus } as CalendarEventType,
              transaction: updatedTransaction,
            }
          : prev,
      );
    },
    [applyAppointmentStatus],
  );

  // The drawer changed the appointment's forms (e.g. removed one): sync the
  // calendar store and the shown appointment.
  const handlePreviewFormsChange = useCallback(
    (forms: AppointmentFormType[]) => {
      const transaction = previewDataRef.current?.transaction;
      if (!transaction) return;
      void handleTransactionUpdated({ ...transaction, forms });
      setPreviewData((prev) =>
        prev?.transaction?.transaction.id === transaction.transaction.id
          ? { ...prev, transaction: { ...prev.transaction, forms } }
          : prev,
      );
    },
    [handleTransactionUpdated],
  );

  const handlePreviewTagsChange = useCallback(
    (tags: AppointmentTag[]) => {
      const transaction = previewDataRef.current?.transaction;
      if (!transaction) return;
      void handleTransactionUpdated({ ...transaction, tags });
      setPreviewData((prev) =>
        prev?.transaction?.transaction.id === transaction.transaction.id
          ? { ...prev, transaction: { ...prev.transaction, tags } }
          : prev,
      );
    },
    [handleTransactionUpdated],
  );

  // Event selection handler (for clicking on events in calendar). Another
  // appointment re-targets the open drawer; every non-appointment branch
  // must clear `previewData` so the drawer doesn't linger behind a
  // time-block modal / transaction modal / nothing.
  const openEventPreview = useCallback(
    (event: CalendarEventType, anchorEl: HTMLElement | null) => {
      if (event.isTimeBlock) {
        showPreview(null);
        openTimeBlockEditor(event);
        return;
      }

      // For appointments, show preview popover on desktop, open modal directly on mobile
      if ("transactionId" in event && event.transactionId) {
        const transaction = findTransaction(event.transactionId);

        if (transaction) {
          // On mobile, open transaction modal directly
          if (isMobile) {
            showPreview(null);
            openTransactionModal(transaction);
          } else {
            // On desktop, show preview popover
            if (anchorEl) {
              showPreview({
                type: "appointment",
                event,
                transaction,
                anchorEl,
              });
            } else {
              // If no anchor, open transaction modal directly
              showPreview(null);
              openTransactionModal(transaction);
            }
          }
        } else {
          // Transaction not in the store — nothing else opens, so close any
          // existing popover rather than leaving it stuck on the prior event.
          showPreview(null);
        }
      } else {
        // Event without a transactionId — close any stale popover.
        showPreview(null);
      }
    },
    [
      findTransaction,
      isMobile,
      openTransactionModal,
      openTimeBlockEditor,
      showPreview,
    ],
  );

  const handleEventSelect = useCallback(
    (event: CalendarEventType, e?: React.SyntheticEvent) =>
      openEventPreview(event, (e?.currentTarget as HTMLElement) ?? null),
    [openEventPreview],
  );

  // Handle event double click (open modal directly)
  const handleEventDoubleClick = useCallback(
    (event: CalendarEventType) => {
      showPreview(null);

      if (event.isTimeBlock) {
        openTimeBlockEditor(event);
        return;
      }

      if ("transactionId" in event && event.transactionId) {
        const transaction = findTransaction(event.transactionId);
        if (transaction) {
          openTransactionModal(transaction);
        }
      }
    },
    [findTransaction, openTransactionModal, openTimeBlockEditor, showPreview],
  );

  const formsAlreadySent =
    !!previewData?.transaction &&
    sentFormTxnIds.has(previewData.transaction.transaction.id);

  return {
    previewData,
    closePreview,
    formsAlreadySent,
    markFormsSent,
    // Per-transaction "forms sent" (the mobile detail sheet reads it directly).
    sentFormTxnIds,
    handlePreviewEdit,
    handlePreviewStatusUpdate,
    handlePreviewFormsChange,
    handlePreviewTagsChange,
    handleOpenForm,
    handleAddForm,
    handleViewClient,
    handleOpenTransactionModal,
    handleEventSelect,
    /** Same as a click on `event` anchored to `anchorEl` (no DOM event). */
    openEventPreview,
    handleEventDoubleClick,
    // Client drawer
    notesClient,
    notesDrawerOpen,
    clientDrawerTab,
    handleClientDrawerClose,
    handleOpenTimelineTransaction,
    timelineTxnLoading,
    // Add Form/SOAP library
    addFormModalOpen,
    isAssigningForm: addFormForms.assigning,
    handleUseTemplate,
    dismissAddFormModal,
    // FormViewer
    viewFormTransaction,
    viewFormId,
    handleFormViewerChanged,
    handleFormViewerClose,
    // Stash (sub-surfaces such as the booking drawer)
    stashAndClosePreview,
    reopenPreview,
    dropPreviewStash,
  };
}

export type PreviewPopoverState = ReturnType<typeof usePreviewPopover>;
